/**
 * LanguageSwitcher Component
 *
 * A dropdown component for switching between languages.
 * Updates both the URL and i18next language.
 */

import { useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
  LANGUAGES,
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '@/lib/i18n';

interface LanguageSwitcherProps {
  /** Compact mode shows only the flag/icon */
  compact?: boolean;
  /** Align the dropdown menu */
  align?: 'start' | 'center' | 'end';
  /** Additional class names */
  className?: string;
}

/**
 * LanguageSwitcher - allows users to change the site language
 *
 * @example
 * // Full version with language names
 * <LanguageSwitcher />
 *
 * @example
 * // Compact version for mobile
 * <LanguageSwitcher compact />
 */
export function LanguageSwitcher({
  compact = false,
  align = 'end',
  className,
}: LanguageSwitcherProps) {
  const { locale: urlLocale } = useParams<{ locale?: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine current locale
  const currentLocale: SupportedLocale =
    urlLocale && SUPPORTED_LOCALES.includes(urlLocale as SupportedLocale)
      ? (urlLocale as SupportedLocale)
      : DEFAULT_LOCALE;

  const currentLanguage = LANGUAGES.find((l) => l.code === currentLocale) || LANGUAGES[0];

  const switchLanguage = useCallback(
    (newLocale: SupportedLocale) => {
      if (newLocale === currentLocale) return;

      // Build new path
      let newPath = location.pathname;

      // Remove current locale prefix if present
      const localePattern = new RegExp(`^/(${SUPPORTED_LOCALES.join('|')})`);
      newPath = newPath.replace(localePattern, '');

      // Ensure path starts with /
      if (!newPath.startsWith('/')) {
        newPath = '/' + newPath;
      }

      // Add new locale prefix (except for default)
      if (newLocale !== DEFAULT_LOCALE) {
        newPath = `/${newLocale}${newPath}`;
      }

      // Change i18next language
      i18n.changeLanguage(newLocale);

      // Navigate to new path, preserving search params and hash
      navigate(newPath + location.search + location.hash);
    },
    [currentLocale, location.pathname, location.search, location.hash, i18n, navigate]
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={compact ? 'icon' : 'sm'}
          className={className}
          aria-label="Select language"
        >
          {compact ? (
            <Globe className="h-4 w-4" />
          ) : (
            <>
              <Globe className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">{currentLanguage.nativeName}</span>
              <span className="sm:hidden">{currentLanguage.flag}</span>
              <ChevronDown className="h-3 w-3 ml-1 opacity-50" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="min-w-[150px]">
        {LANGUAGES.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => switchLanguage(language.code)}
            className="flex items-center justify-between cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <span>{language.flag}</span>
              <span>{language.nativeName}</span>
            </span>
            {language.code === currentLocale && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSwitcher;
